// @ts-nocheck -- RARE-264 mechanical rename; type this file under RARE-265
import { getRecordConsolePlugin } from "@rrweb/rrweb-plugin-console-record";
import { getRecordNetworkPlugin } from "@rrweb/rrweb-plugin-network-record";
import * as Sentry from "@sentry/react";
import { record } from "rrweb";
import { getAuthToken } from "../auth";
import {
  MAX_BATCH_BYTES as MAX_PENDING_BATCH_BYTES,
  savePendingBatch,
  takePendingBatches,
} from "./pending-batches";
import { getRecordingConfig, shouldRecord } from "./recording-config";

let stopRecordingFn = null;
// Entries, not bare events: each carries the encoded size it contributes to a
// flush body, measured once on emit so neither the byte-aware split nor the
// buffer-full eviction has to serialise the buffer again.
let bufferedEntries = [];
let bufferedBytes = 0;
let flushChain = Promise.resolve();
let flushTimer = null;
// Per recording, not one global counter, and held in an object so a flush can
// hold on to the one it was scheduled against. `startRecording` puts a fresh one
// here. A global was safe only while a stopped recording could not still have a
// flush in flight; now that stopRecording's last flush really goes out, and
// auth-context stops and restarts recording in one go whenever the user changes,
// the old recording's final flush would otherwise draw its number from the new
// recording's counter and take sequence 0 off it. A recording whose first chunk
// is chunk-001 is the unplayable recording this ticket started from.
let sequenceCounter = { next: 0 };
let currentSessionId = null;
let currentUserId = null;
let currentUserEmail = null;
let hasErrorsInBatch = false;

function generateSessionId() {
  return crypto.randomUUID();
}

function getCurrentPageUrl() {
  return window.location.pathname;
}

export function sanitiseNetworkRequest(request) {
  const url = new URL(request.name, window.location.origin);
  const queryParameterNames = [...url.searchParams.keys()].map(encodeURIComponent);
  const urlPath = queryParameterNames.length
    ? `${url.pathname}?${queryParameterNames.join("&")}`
    : url.pathname;

  return {
    method: request.method,
    urlPath,
    statusCode: request.status,
    duration: request.duration,
    initiatorType: request.initiatorType === "xmlhttprequest" ? "xhr" : "fetch",
  };
}

// The two events a replay cannot start without. rrweb emits a type-4 Meta
// carrying the viewport dimensions and then a type-2 FullSnapshot carrying the
// initial DOM, as the first two events of a recording. Every type-3 incremental
// after them is a mutation applied *to* that DOM, so a session that loses them
// is not a shorter recording, it is an unplayable one: the admin list shows a
// healthy row with a non-zero event count and the support user gets a blank
// player. That is the shape RARE-321's audit found in production, in
// d78c90a0-a2b6-430e-aae3-1468b8738116 among others.
function isEssentialEvent(event) {
  return event?.type === 2 || event?.type === 4;
}

function isConsoleErrorEvent(event) {
  if (event.type !== 6) return false;
  const plugin = event.data && event.data.plugin;
  if (plugin !== "rrweb/console@1") return false;
  const level = event.data.payload && event.data.payload.level;
  return level === "error";
}

// `pageUrl` is passed in rather than read here. A flush builds this body more
// than once, to measure an envelope and then to send it, and on the unload path
// three times, so reading `window.location.pathname` per call let an SPA route
// change land between the measurement and the send. The measured envelope was
// then smaller than the one that actually went around the body, and every size
// budget derived from it was wrong by the difference. Callers read the path once
// per flush and hand the same value to every invocation.
//
// `sessionId` is passed in for the same reason, but a sharper one: stopRecording
// nulls `currentSessionId` while a flush it scheduled is still queued, so a flush
// that read the global would address the wrong session or no session at all.
// Every flush now carries the id it was scheduled for.
//
// `userId` and `userEmail` are carried for the same reason and a third one.
// `auth-context` stops and restarts recording in a single frame when the user
// changes, so the old recording's final flush ran with the *new* user's id
// already in the global and labelled one user's events with another's. Ingest
// also compares the batch's user id against the authenticated caller and
// answers 403 when they differ, so reading the global here is both a
// mislabelling and, on the recovered batches sessionStorage hands back, a
// guaranteed refusal.
function buildIngestBody({
  events,
  sequenceNumber: batchSequence,
  hasErrors,
  includeUserAgent,
  pageUrl,
  sessionId,
  userId,
  userEmail,
}) {
  return JSON.stringify({
    sessionId,
    userId: userId || "anonymous",
    // Left to drop out of the JSON when it is absent, not coerced to null:
    // ingest validates it as `z.string().optional()`, which a literal null
    // fails, so an anonymous recording would 400 on every flush.
    userEmail,
    sequenceNumber: batchSequence,
    events,
    pageUrl,
    hasErrors,
    ...(includeUserAgent ? { userAgent: navigator.userAgent } : {}),
  });
}

// The body around the events, sized as an upper bound: the widest sequence
// number JSON will ever hold, and the userAgent that only the first batch of a
// session carries. Sizing chunks against the upper bound means a chunk cannot
// grow past its budget once the real envelope goes around it.
function measureEnvelopeBytes({ hasErrors, pageUrl, sessionId, userId, userEmail }) {
  return encodedLength(
    buildIngestBody({
      events: [],
      sequenceNumber: Number.MAX_SAFE_INTEGER,
      hasErrors,
      includeUserAgent: true,
      pageUrl,
      sessionId,
      userId,
      userEmail,
    }),
  );
}

// Splits sized events into the fewest consecutive runs whose serialised body
// each fits `limit`, oldest first so playback order survives. An event that
// does not fit a run of its own is still given one and goes over budget: a
// single rrweb event cannot be divided, and flushEvents has already discarded
// the ones the recorder would refuse outright.
//
// Sizes are added up rather than re-serialised per candidate boundary: the
// encoded length of a JSON array is its envelope, plus each element, plus one
// comma between them.
export function chunkEntriesByByteLimit(entries, envelopeBytes, limit) {
  const chunks = [];
  let current = [];
  let total = envelopeBytes;

  for (const entry of entries) {
    const cost = current.length > 0 ? entry.size + 1 : entry.size;

    if (current.length > 0 && total + cost > limit) {
      chunks.push(current);
      current = [entry];
      total = envelopeBytes + entry.size;
      continue;
    }

    current.push(entry);
    total += cost;
  }

  if (current.length > 0) {
    chunks.push(current);
  }

  return chunks;
}

// Reports whether the batch landed rather than swallowing the answer. This
// returned nothing and only warned, so flushEvents had no way to tell a
// delivered batch from a refused one and treated both as spent: the events were
// already gone from the buffer and the sequence number already committed. A 413,
// a network blip, a pod eviction or an expired token therefore cost the batch
// permanently. `delivered` is true only for a 2xx the browser actually returned.
async function postBatch(batch, hasErrors) {
  const config = getRecordingConfig();
  const body = buildIngestBody({
    events: batch.events,
    sequenceNumber: batch.sequenceNumber,
    hasErrors,
    includeUserAgent: batch.sequenceNumber === 0,
    pageUrl: batch.pageUrl,
    sessionId: batch.sessionId,
    userId: batch.userId,
    userEmail: batch.userEmail,
  });
  const bytes = encodedLength(body);

  // A request that never settles holds the flush chain open for the rest of the
  // session and the buffer grows behind it, so it is given a deadline. The abort
  // arrives as a rejected fetch and is handled as one more way of not landing.
  const controller = new AbortController();
  const deadline = setTimeout(() => controller.abort(), config.flushRequestTimeoutMs);

  try {
    const token = getAuthToken();
    const response = await fetch(
      `${config.recorderUrl}/api/v1/sessions/${batch.sessionId}/events`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body,
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      // The size goes in the line because a 413 here is a size problem and
      // reading it out of the browser console is the only way anyone will see
      // which batch was refused.
      console.warn("[rrweb] Failed to flush events:", response.status, {
        sequenceNumber: batch.sequenceNumber,
        eventCount: batch.events.length,
        bytes,
      });
      return {
        delivered: false,
        reason: `http-${response.status}`,
        detail: { status: response.status, bytes, sequenceNumber: batch.sequenceNumber },
      };
    }

    return { delivered: true };
  } catch (err) {
    const aborted = controller.signal.aborted || err?.name === "AbortError";
    console.warn("[rrweb] Failed to flush events:", err?.message ?? err);
    return {
      delivered: false,
      reason: aborted ? "request-aborted" : "fetch-threw",
      detail: {
        bytes,
        sequenceNumber: batch.sequenceNumber,
        timeoutMs: aborted ? config.flushRequestTimeoutMs : undefined,
        message: String(err?.message ?? err),
      },
    };
  } finally {
    clearTimeout(deadline);
  }
}

function flushEvents() {
  if (bufferedEntries.length === 0 || !currentSessionId) return Promise.resolve();

  const entries = bufferedEntries;
  const errorsInBatch = hasErrorsInBatch;
  const sessionId = currentSessionId;
  const userId = currentUserId;
  const userEmail = currentUserEmail;
  const counter = sequenceCounter;
  bufferedEntries = [];
  bufferedBytes = 0;
  hasErrorsInBatch = false;

  return flushBatch({ entries, errorsInBatch, sessionId, userId, userEmail, counter });
}

// The periodic path, and the final flush stopRecording schedules. It used to
// empty the buffer and commit every chunk's sequence number before a single byte
// went out, and then ignore whether any of it landed. This is the path that
// sends the majority of chunks on a normal session, sequence 0 among them, so
// that was the common instance of the loss RARE-321 exists to close, not the
// rare one.
//
// A chunk that did not land now hands its events and its sequence number back,
// by the same requeue the unload path uses. The chunks behind it are handed back
// unsent rather than spent: the refusals that happen here are a 413 on an
// oversized body, an expired token, a pod eviction mid-rollout and a lost
// connection, and every one of those is about as likely to refuse the next chunk
// as it was this one. Stopping also keeps the requeue simple, because each event
// is in exactly one place at all times: either in a chunk this loop still holds,
// or back in the buffer, never both.
//
// `pageUrl` is accepted rather than always read here, and `attempts` exists at
// all, for the recovered batches `startRecording` replays out of
// sessionStorage: those events happened on a page the tab has since left, so
// the current path would mislabel them, and a retry has to know how many times
// this batch has already been refused or it would retry for the life of the tab.
async function flushBatch({
  entries,
  errorsInBatch,
  sessionId,
  userId,
  userEmail,
  counter,
  // Read once, before the first await, and carried on every chunk: the awaits
  // below span several requests and the user can navigate across them.
  pageUrl = getCurrentPageUrl(),
  attempts = 0,
}) {
  const config = getRecordingConfig();
  const envelopeBytes = measureEnvelopeBytes({
    hasErrors: errorsInBatch,
    pageUrl,
    sessionId,
    userId,
    userEmail,
  });
  const sendable = [];

  for (const entry of entries) {
    if (envelopeBytes + entry.size > config.serverMaxBatchBytes) {
      console.warn(
        `[rrweb] Dropped a single ${entry.size} byte event: one event cannot be split and the recorder refuses a batch over ${config.serverMaxBatchBytes} bytes`,
      );
      diagnostics.droppedEvents += 1;
      continue;
    }
    sendable.push(entry);
  }

  if (sendable.length === 0) return;

  const chunks = chunkEntriesByByteLimit(sendable, envelopeBytes, config.maxBatchBytes);

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    // Reserved per chunk, immediately before its own request, rather than all of
    // them up front. Reserving up front meant a chunk that failed could never
    // give its number back, because a later chunk had already claimed a higher
    // one and releaseSequenceNumber will only rewind the newest. Flushes are
    // chained, so within a flush the numbers still come out consecutive.
    const batchSequence = reserveSequenceNumber(counter);
    const outcome = await postBatch(
      {
        events: chunk.map((entry) => entry.event),
        sequenceNumber: batchSequence,
        pageUrl,
        sessionId,
        userId,
        userEmail,
      },
      errorsInBatch,
    );

    if (outcome.delivered) continue;

    const unsent = chunks.slice(i).flat();
    abandonBatch({
      path: "periodic",
      reason: outcome.reason,
      sessionId,
      userId,
      counter,
      entries: unsent,
      errorsInBatch,
      batchSequence,
      pageUrl,
      attempts,
      detail: { ...outcome.detail, chunksAbandoned: chunks.length - i },
      message: `[rrweb] Flush refused (${outcome.reason}): ${unsent.length} events from the remaining ${chunks.length - i} chunks did not go out, sequence ${batchSequence} released`,
    });
    return;
  }
}

// Splitting a flush turned one request into several, and a burst of events can
// start a flush while an earlier one is still awaiting its fetch. Chaining them
// keeps a single ingest request in flight, so the order on the wire matches the
// sequence numbers and a busy page cannot open a dozen uploads at once. The
// chain is never left rejected, or one failure would strand every flush after
// it; postBatch already handles the failures that are expected.
function scheduleFlush() {
  flushChain = flushChain.then(() =>
    flushEvents().catch((err) => {
      console.warn("[rrweb] Flush failed:", err?.message ?? err);
    }),
  );
  return flushChain;
}

function handleVisibilityChange() {
  if (document.visibilityState === "hidden") {
    handleBeforeUnload();
  }
}

// A keepalive fetch is the only way to carry the bearer header through an
// unload, but Chrome's ~64 KiB keepalive allowance is granted to the page as a
// whole, shared across every keepalive request still in flight, not to each
// request. A fixed 60 KiB per-request budget therefore guarantees a refusal the
// moment two of them overlap, and they do overlap here: visibilitychange fires
// on a tab switch, beforeunload can follow before the first request has
// settled, and Sentry sends its own keepalive requests out of the same
// allowance. So the budget is held as one pool. Each flush may only use what is
// currently unreserved, and hands its reservation back when its request
// settles, which is the browser's own signal that the bytes are no longer in
// flight. Nothing is reset on start/stop: a request outliving a recording still
// occupies the browser's quota, and its own settle handler is what frees it.
const KEEPALIVE_QUOTA_BYTES = 60 * 1024;
let keepaliveBytesReserved = 0;

export const RECORDER_FLUSH_REFUSED_EVENT = "rare:recorder-flush-refused";

// How a caller ever finds out. `console.warn` was the only signal before, and
// on a page being unloaded it reaches nobody: devtools is shut, the page is
// gone a moment later, and nothing retries. So a refusal is recorded three ways
// that outlive the flush.
//
//  1. Sentry, which is the only sink here that survives the page. It is
//     initialised in production in src/index.tsx, and is a no-op elsewhere.
//  2. These counters, readable from the console or a test via
//     `getRecorderDiagnostics()`.
//  3. A `rare:recorder-flush-refused` DOM event, so anything in the app can
//     react without importing this module.
//
// The periodic path reports the same three ways. Its `console.warn` was no more
// readable than the unload one: nobody has devtools open on the session that is
// losing chunks, and that path refuses far more often because it carries far
// more of the traffic.
//
// None of those is the real remedy. The remedy is that a refused flush now puts
// its events back, so the next flush carries them and the refusal costs nothing
// but a delay. These exist so that a refusal that does become a loss, because
// the page really did go away, is at least visible afterwards.
const diagnostics = {
  refusedFlushes: 0,
  requeuedEvents: 0,
  evictedEvents: 0,
  // Times a bound was reached and nothing could be shed to meet it, because
  // every entry left was a Meta or a FullSnapshot. Not a loss, and not an
  // error: it is the buffer deliberately sitting over its cap rather than
  // destroying the events replay needs. Counted because a number that climbs
  // says the buffer is under sustained pressure.
  evictionsBlocked: 0,
  // Events handed to sessionStorage because the recording they belong to had
  // already stopped, for the next recording in this tab to retry. Delayed, not
  // lost, unless the tab itself goes.
  persistedEvents: 0,
  // Events read back out of sessionStorage and resent. The counter is on the
  // pick-up, not the delivery: whether the retry lands is `refusedFlushes`.
  recoveredEvents: 0,
  // Events a refusal could not hand back and could not persist either: storage
  // was unavailable or full, or the batch had already used up its retries. This
  // and `droppedEvents` are the counters that measure real loss rather than
  // delay.
  abandonedEvents: 0,
  // Single events too large for the recorder to accept at all.
  droppedEvents: 0,
  lastRefusal: null,
};

export function getRecorderDiagnostics() {
  // `keepaliveBytesReserved` is reported because a reservation that is taken and
  // not handed back silently shrinks the allowance every later unload flush has
  // to fit into, until none of them fit at all. A leak there is invisible from
  // the outside and degrades exactly the path that cannot retry.
  return {
    ...diagnostics,
    keepaliveBytesReserved,
    bufferedEvents: bufferedEntries.length,
    bufferedBytes,
  };
}

function recordRefusal(path, reason, detail) {
  diagnostics.refusedFlushes += 1;
  diagnostics.lastRefusal = { path, reason, ...detail };

  // `detail` carries the session the batch belonged to. It is spread last so it
  // wins over the live one, which for a final or a recovered flush is a
  // different recording entirely and was the id this used to report.
  Sentry.captureMessage(`[rrweb] ${path} flush refused: ${reason}`, {
    level: "warning",
    extra: { sessionId: currentSessionId, path, ...detail },
  });

  window.dispatchEvent(
    new CustomEvent(RECORDER_FLUSH_REFUSED_EVENT, { detail: { path, reason, ...detail } }),
  );
}

// Merged rather than prepended. Requeued entries are almost always older than
// everything buffered, and then this is a concatenation, but a flush that was
// trimmed to fit and then refused hands its two halves back separately, and the
// buffer has to come out in the order the events happened: chunking walks it in
// order and eviction drops from the oldest end.
function mergeEntriesByTimestamp(a, b) {
  const merged = [];
  let i = 0;
  let j = 0;

  while (i < a.length && j < b.length) {
    merged.push(a[i].event.timestamp <= b[j].event.timestamp ? a[i++] : b[j++]);
  }
  while (i < a.length) merged.push(a[i++]);
  while (j < b.length) merged.push(b[j++]);

  return merged;
}

// Puts entries back in the buffer so a later flush can carry them. `emit`'s
// eviction rule is applied afterwards for the same reason it exists there: a
// page that keeps refusing must not grow the buffer without bound.
function requeueEntries(entries, errorsInBatch) {
  if (entries.length === 0) return;

  bufferedEntries = mergeEntriesByTimestamp(entries, bufferedEntries);
  for (const entry of entries) {
    bufferedBytes += entry.size;
  }
  if (errorsInBatch) {
    hasErrorsInBatch = true;
  }
  diagnostics.requeuedEvents += entries.length;

  trimBufferToBounds();
}

// Drops the oldest entry the recording can afford to lose, and reports whether
// it found one. Essential entries are stepped over rather than dropped, which
// is the whole of the fix: eviction used to `shift()` from the front of the
// buffer, and the front of the buffer is exactly where rrweb puts the Meta and
// the FullSnapshot. Worse, eviction only ever runs while the buffer is under
// pressure, and the buffer is only under pressure while flushing is failing or
// slow, so the one situation that triggered it was the one where losing those
// two costs the entire session rather than a few mouse moves.
//
// Everything expendable still goes oldest-first, matching the order chunking
// walks the buffer in and the order the old rule used.
function evictOldestExpendableEntry() {
  for (let i = 0; i < bufferedEntries.length; i++) {
    if (isEssentialEvent(bufferedEntries[i].event)) continue;

    bufferedBytes -= bufferedEntries[i].size;
    bufferedEntries.splice(i, 1);
    diagnostics.evictedEvents += 1;
    return true;
  }

  // Nothing expendable left. Not a loss and not an error: it is the buffer
  // sitting over its bound rather than destroying the events replay needs.
  // Counted so that a number which climbs says the buffer is under sustained
  // pressure, which is the only symptom this trade has.
  diagnostics.evictionsBlocked += 1;
  return false;
}

// Bounded in entries and in bytes. The count alone is not a bound on memory:
// maxBufferSize entries of a megabyte apiece is half a gigabyte held in a tab,
// and a server refusing every flush is precisely what drives towards it. Never
// down to nothing: a lone entry over the byte cap is flushBatch's problem, not
// this one's, and dropping it here would hide it.
//
// Giving up when only essential entries are left is not an unbounded buffer.
// rrweb is started here without a checkout interval, so a recording emits one
// Meta and one FullSnapshot and never another of either; two entries is the
// most this can be asked to hold on to. Preferring those two over the bound is
// the right trade even at a FullSnapshot's size, because they are the
// difference between a session that replays and one that does not.
function trimBufferToBounds() {
  const { maxBufferSize, maxBufferBytes } = getRecordingConfig();

  while (
    bufferedEntries.length > maxBufferSize ||
    (bufferedEntries.length > 1 && bufferedBytes > maxBufferBytes)
  ) {
    if (!evictOldestExpendableEntry()) return;
  }
}

// The one routine both flush paths abandon a batch through: the events go back
// in the buffer, the sequence number goes back out of circulation, and the
// refusal is reported. Written for the unload path first; the periodic path
// calls exactly this rather than a second mechanism of its own.
//
// The requeue is not safe once the recording has moved on: it would put one
// recording's events into the next one's buffer, to be uploaded under the wrong
// session id, which is worse than losing them. So it is conditional on the batch
// still belonging to the live recording.
//
// When it does not, the batch goes to sessionStorage instead of to the floor.
// That case is the final flush of a recording, which `stopRecording` schedules
// after it has already detached the buffer, and it is the flush least able to
// afford the loss: it is the largest, nothing follows it to compensate, and on a
// short session it is carrying the Meta and FullSnapshot too. The stored batch
// keeps its own session id and its own sequence number, so the next recording in
// this tab resends it into the recording it belongs to rather than adopting its
// events. See pending-batches.ts.
//
// Only if that fails as well, because storage is unavailable or full or the
// batch has used up its retries, are the events counted as lost. All three
// dispositions are said out loud, because they are genuinely different outcomes
// and reporting a recovery that did not happen is the failure mode these tickets
// exist to close.
//
// The release needs no such condition, because the counter travels with the
// batch rather than being looked up. Handing a number back to a counter whose
// recording has ended changes nothing; the live recording has its own.
function abandonBatch({
  path,
  reason,
  sessionId,
  userId,
  counter,
  entries,
  errorsInBatch,
  batchSequence,
  pageUrl,
  attempts = 0,
  detail,
  message,
}) {
  // The number always goes back, because it goes back to the counter this batch
  // drew it from. If that recording has since ended, the counter went with it
  // and this is a no-op rather than a rewind of the live one.
  releaseSequenceNumber(counter, batchSequence);

  let disposition;
  let persistedEvents = 0;

  if (sessionId === currentSessionId) {
    requeueEntries(entries, errorsInBatch);
    disposition = "requeued";
  } else {
    // Trimmed here rather than in the store, because which events a batch can
    // afford to lose is a fact about rrweb and not about sessionStorage.
    const { events: holdable } = trimEventsToStorageBudget(
      entries.map((entry) => entry.event),
      MAX_PENDING_BATCH_BYTES,
    );

    // The sequence number is the one this batch already reserved, not a fresh
    // one. Its counter has just taken it back and that counter is going nowhere
    // else, so the chunk lands at the key it was always going to have.
    persistedEvents = savePendingBatch({
      sessionId,
      userId,
      sequenceNumber: batchSequence,
      pageUrl,
      hasErrors: errorsInBatch,
      events: holdable,
      attempts: attempts + 1,
    });

    if (persistedEvents > 0) {
      diagnostics.persistedEvents += persistedEvents;
      // Trimming to the storage budget can leave some behind, and those are as
      // gone as they ever were.
      diagnostics.abandonedEvents += entries.length - persistedEvents;
      disposition = "persisted";
    } else {
      diagnostics.abandonedEvents += entries.length;
      disposition = "abandoned";
    }
  }

  // The disposition is said here rather than by the caller, because the caller
  // cannot know it. A line claiming a recovery on the one path where the events
  // really are lost is worse than no line at all.
  if (message) {
    const outcome = {
      requeued: "requeued for the next flush",
      persisted: `held for the next recording in this tab to retry (${persistedEvents} of ${entries.length} events)`,
      abandoned: "the recording has already stopped and they could not be held, so they are lost",
    }[disposition];
    console.warn(`${message}; ${outcome}`);
  }

  recordRefusal(path, reason, {
    sessionId,
    eventCount: entries.length,
    // Kept as a boolean as well as the disposition: a caller asking "did the
    // live buffer get these back" is asking something narrower than "were they
    // recovered at all", and the answer differs on the persisted path.
    requeued: disposition === "requeued",
    disposition,
    persistedEvents,
    ...detail,
  });
}

// The sequence number names the object the recorder stores the chunk in,
// chunk-NNN.json.gz, so two batches sent under one number overwrite each other
// and the first one's events are gone. It is therefore taken out of circulation
// before the request goes out, so a concurrent flush cannot claim it, and only
// becomes permanent once the browser has accepted the request. A refusal hands
// it back, but only while it is still the newest: if another flush has claimed
// one since, rewinding would hand out a number already spoken for, and trading
// a harmless gap for an overwrite is a bad trade. Gaps are harmless because the
// recorder reassembles a session from the chunks that exist, in key order, and
// the replayer sorts by timestamp.
function reserveSequenceNumber(counter) {
  return counter.next++;
}

function releaseSequenceNumber(counter, reserved) {
  if (counter.next === reserved + 1) {
    counter.next = reserved;
  }
}

// Byte length of a string as it goes on the wire, which is what every budget
// here is stated in. `String.length` counts UTF-16 code units and would
// under-count every non-ASCII character in a recorded page.
function encodedLength(value) {
  return new TextEncoder().encode(value).length;
}

// Trims a batch to the bytes sessionStorage will be asked to hold, by the same
// rule the live buffer uses: drop the oldest expendable event and stop when only
// the essential ones are left. A held batch without its Meta and FullSnapshot is
// a batch not worth holding, so those are never what is traded away for room.
//
// Sizes are measured once and added up rather than re-serialising the array per
// candidate boundary: this runs while the page is already leaving. The encoded
// length of a JSON array is its two brackets, plus each element, plus one comma
// between them.
export function trimEventsToStorageBudget(events, budget) {
  const entries = events.map((event) => ({ event, size: encodedLength(JSON.stringify(event)) }));
  let total = entries.reduce((sum, entry) => sum + entry.size, 2);
  if (entries.length > 1) total += entries.length - 1;

  let dropped = 0;
  while (total > budget && entries.length > 0) {
    const index = entries.findIndex((entry) => !isEssentialEvent(entry.event));
    if (index === -1) break;
    total -= entries[index].size + (entries.length > 1 ? 1 : 0);
    entries.splice(index, 1);
    dropped += 1;
  }

  return { events: entries.map((entry) => entry.event), dropped };
}

// Picks the longest run of newest events whose serialised payload fits inside
// `limit`. Newest rather than oldest because the tail of the buffer is what was
// on screen when the user left, which is the part support needs.
//
// Calling `buildBody` once per candidate boundary would mean O(n) full
// serialisations during unload, so size each event once and add the sizes up
// instead: the encoded length of a JSON array is exactly its envelope, plus
// each element, plus one comma between them.
export function fitEventsToKeepaliveLimit(events, buildBody, limit = KEEPALIVE_QUOTA_BYTES) {
  const whole = buildBody(events);
  if (encodedLength(whole) <= limit) {
    return { body: whole, events, dropped: 0 };
  }

  const envelope = encodedLength(buildBody([]));
  let total = envelope;
  let start = events.length;

  for (let i = events.length - 1; i >= 0; i--) {
    const separator = start < events.length ? 1 : 0;
    const cost = encodedLength(JSON.stringify(events[i])) + separator;
    if (total + cost > limit) break;
    total += cost;
    start = i;
  }

  const kept = events.slice(start);
  return { body: kept.length > 0 ? buildBody(kept) : null, events: kept, dropped: start };
}

function handleBeforeUnload() {
  if (bufferedEntries.length === 0 || !currentSessionId) return;

  const config = getRecordingConfig();

  // Taken, not destroyed. Everything below either puts bytes on the wire or
  // puts these back; the version of this that cleared the buffer here and then
  // returned early was the loss path.
  const entries = bufferedEntries;
  const errorsInBatch = hasErrorsInBatch;
  const sessionId = currentSessionId;
  const userId = currentUserId;
  const userEmail = currentUserEmail;
  const counter = sequenceCounter;
  bufferedEntries = [];
  bufferedBytes = 0;
  hasErrorsInBatch = false;

  // One read, shared by all three buildBody calls below.
  const pageUrl = getCurrentPageUrl();
  const batchSequence = reserveSequenceNumber(counter);

  const buildBody = (events) =>
    buildIngestBody({
      events,
      sequenceNumber: batchSequence,
      hasErrors: errorsInBatch,
      includeUserAgent: batchSequence === 0,
      pageUrl,
      sessionId,
      userId,
      userEmail,
    });

  // Only what no other in-flight keepalive request has already reserved.
  const available = KEEPALIVE_QUOTA_BYTES - keepaliveBytesReserved;
  const { body, dropped } = fitEventsToKeepaliveLimit(
    entries.map((entry) => entry.event),
    buildBody,
    available,
  );

  // Nothing fits: either no single event is small enough, or another keepalive
  // request currently holds the quota. No bytes go on the wire, so every event
  // goes back, the sequence number goes back with them, and the next flush
  // carries the lot. This branch used to keep the sequence number and throw the
  // events away, which on the first flush of a recording meant throwing away
  // the rrweb type-2 FullSnapshot that replay cannot start without.
  if (!body) {
    abandonBatch({
      path: "unload",
      reason: "no-keepalive-room",
      sessionId,
      userId,
      counter,
      entries,
      errorsInBatch,
      batchSequence,
      pageUrl,
      detail: {
        availableBytes: available,
        quotaBytes: KEEPALIVE_QUOTA_BYTES,
        reservedBytes: keepaliveBytesReserved,
      },
      message: `[rrweb] Dropped the final flush entirely: nothing fits the ${available} bytes left of the keepalive quota; ${entries.length} events did not go out`,
    });
    return;
  }

  // The trimmed events are the oldest, which is where rrweb puts the type-2
  // FullSnapshot, and no bytes of them reach the wire, so they go straight back
  // rather than being discarded. A later chunk is a fine home for them: the
  // recorder reassembles a session from whichever chunks exist and the replayer
  // sorts by timestamp, so a snapshot arriving out of chunk order still plays.
  const sent = entries.slice(dropped);

  if (dropped > 0) {
    requeueEntries(entries.slice(0, dropped), errorsInBatch);
    console.warn(
      `[rrweb] Final flush of ${entries.length} events exceeded the ${available} bytes left of the keepalive quota; dropped the oldest ${dropped} from this request and requeued them for the next flush`,
    );
  }

  const bytes = encodedLength(body);
  keepaliveBytesReserved += bytes;

  const token = getAuthToken();
  const request = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    keepalive: true,
  };

  // The captured id, not the global, for the same reason every other read in
  // this function was moved off the global: the batch is addressed to the
  // recording it was taken from, and nothing here may depend on that still
  // being the live one.
  const url = `${config.recorderUrl}/api/v1/sessions/${sessionId}/events`;

  // A refused keepalive request is reported as a rejected promise, but a
  // browser that refuses it outright, or a stubbed fetch, can throw here
  // instead. Either way nothing reached the wire.
  let pending;
  try {
    pending = fetch(url, request);
  } catch (err) {
    keepaliveBytesReserved -= bytes;
    console.warn("[rrweb] Failed to flush events on unload:", err?.message ?? err);
    abandonBatch({
      path: "unload",
      reason: "fetch-threw",
      sessionId,
      counter,
      entries: sent,
      errorsInBatch,
      batchSequence,
      pageUrl,
      detail: { bytes, message: String(err?.message ?? err) },
    });
    return;
  }

  pending.then(
    (response) => {
      keepaliveBytesReserved -= bytes;

      // A settled request is not a delivered one, and this is where that
      // distinction was missing. `pending.then(onSuccess, onError)` separates
      // the fetch promise resolving from it rejecting, and an HTTP refusal
      // resolves: a 413 on an oversized body, a 401 on a token that expired
      // while the tab sat open, a 503 from a pod going down mid-rollout all
      // arrive here, not in the handler below. Every one of them was counted as
      // delivered, so the events were dropped, the sequence number was kept,
      // and all three sinks stayed silent. That is the exact loss RARE-321
      // exists to close, on the one path that cannot retry.
      //
      // `postBatch` has had this check throughout; the unload path never got
      // it. It is the same judgement either way, so the outcome is the same:
      // the response was a refusal, nothing was stored, the events go back and
      // the sequence number goes back with them.
      //
      // A resolution with no readable `ok` is treated as a refusal too. The
      // only honest reading of a response we cannot inspect is that we do not
      // know the batch landed, and on this path guessing wrong loses events.
      if (!response?.ok) {
        const status = response?.status;
        console.warn("[rrweb] Failed to flush events on unload:", status ?? "unreadable response");
        abandonBatch({
          path: "unload",
          reason: status ? `http-${status}` : "unreadable-response",
          sessionId,
          userId,
          counter,
          entries: sent,
          errorsInBatch,
          batchSequence,
          pageUrl,
          detail: { bytes, status },
        });
      }
    },
    (err) => {
      // This used to swallow, and then it logged and gave up. A rejected
      // unload flush put nothing on the wire, so the events go back and the
      // sequence number goes back with them: refusal is retryable, not fatal.
      keepaliveBytesReserved -= bytes;
      console.warn("[rrweb] Failed to flush events on unload:", err?.message ?? err);
      abandonBatch({
        path: "unload",
        reason: "fetch-rejected",
        sessionId,
        userId,
        counter,
        entries: sent,
        errorsInBatch,
        batchSequence,
        pageUrl,
        detail: { bytes, message: String(err?.message ?? err) },
      });
    },
  );
}

export function startRecording(userId, userEmail) {
  const config = getRecordingConfig();

  if (!shouldRecord(config)) return;

  if (stopRecordingFn) {
    stopRecording();
  }

  currentSessionId = generateSessionId();
  currentUserId = userId;
  currentUserEmail = userEmail;
  sequenceCounter = { next: 0 };
  bufferedEntries = [];
  bufferedBytes = 0;
  flushChain = Promise.resolve();
  hasErrorsInBatch = false;

  stopRecordingFn = record({
    emit(event) {
      const config = getRecordingConfig();
      const entry = { event, size: encodedLength(JSON.stringify(event)) };

      // Appended first, then trimmed, so this path and the requeue path bound
      // the buffer by one rule rather than two. It had its own: it evicted by
      // entry count only, which is no bound on memory at all, and it shifted
      // from the front, which is where the Meta and the FullSnapshot are.
      bufferedEntries.push(entry);
      bufferedBytes += entry.size;
      trimBufferToBounds();

      if (isConsoleErrorEvent(event)) {
        hasErrorsInBatch = true;
      }

      // RARE-303: the byte trigger is the keepalive tail budget, not the much
      // larger per-request split budget. What is buffered when the page goes
      // away is what the unload flush has to fit into a keepalive request, so
      // holding the buffer near that size during the session is what keeps the
      // final flush deliverable. Trimming at unload could only ever choose what
      // to lose; this is what stops there being anything to lose.
      if (
        bufferedEntries.length >= config.batchSize ||
        bufferedBytes >= config.keepaliveTailBytes
      ) {
        scheduleFlush();
      }
    },
    maskAllInputs: true,
    sampling: {
      mousemove: 50,
      mouseInteraction: true,
      scroll: 150,
      media: 800,
      input: "last",
    },
    recordCanvas: false,
    collectFonts: false,
    inlineStylesheet: true,
    inlineImages: false,
    plugins: [
      getRecordConsolePlugin({
        level: ["log", "info", "warn", "error", "debug"],
        lengthThreshold: 1000,
        stringifyOptions: {
          stringLengthLimit: 500,
          numOfKeysLimit: 50,
        },
      }),
      getRecordNetworkPlugin({
        initiatorTypes: ["fetch", "xmlhttprequest"],
        recordHeaders: { request: false, response: false },
        recordBody: false,
        recordInitialRequests: false,
        transformRequestFn: sanitiseNetworkRequest,
      }),
    ],
  });

  flushTimer = setInterval(() => {
    if (bufferedEntries.length > 0) {
      scheduleFlush();
    }
  }, config.flushIntervalMs);

  window.addEventListener("beforeunload", handleBeforeUnload);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // After the new recording is fully set up, so a retry cannot run against a
  // half-initialised one, and on the chain rather than awaited, because
  // `startRecording` is called from a React effect and must not block. The
  // chain is the right place for it on its own merits too: recovered batches
  // then queue behind the live recording's flushes instead of racing them, and
  // one ingest request is in flight at a time as it is everywhere else here.
  recoverPendingBatches(userId);

  console.log("[rrweb] Recording started", { sessionId: currentSessionId, userId });
}

// Picks up whatever the last recording in this tab could not deliver and sends
// it under the session it belongs to.
//
// The batches are taken out of storage rather than copied out. A retry that
// fails saves itself again through `abandonBatch`, with its attempt count
// raised, and that is what bounds the retrying; leaving the record in place
// would instead put two copies of the same events in flight.
//
// Only batches recorded for this user are taken. The retry posts under the
// authenticated user's token and ingest returns 403 when the batch's user id
// does not match the caller, so someone else's batch would fail every attempt
// and spend them all doing it. It stays in storage for its own user instead.
// On the case this exists for, a page reload, the user is the same one.
function recoverPendingBatches(userId) {
  const { batches, discardedEvents } = takePendingBatches(userId);

  if (discardedEvents > 0) {
    // Out of attempts or out of time. Counted as lost here rather than left to
    // look like a pending retry that is still going to happen.
    diagnostics.abandonedEvents += discardedEvents;
    console.warn(
      `[rrweb] Gave up on ${discardedEvents} held events: too old, or already refused too many times`,
    );
  }

  for (const batch of batches) {
    diagnostics.recoveredEvents += batch.events.length;
    console.log(`[rrweb] Retrying ${batch.events.length} events held from an earlier recording`, {
      sessionId: batch.sessionId,
      sequenceNumber: batch.sequenceNumber,
    });

    // A counter of its own, starting at the number the batch already reserved.
    // The recording that issued it has gone and took its counter with it, so
    // nothing else can claim that number, and the chunk lands at the key it was
    // always going to have.
    const counter = { next: batch.sequenceNumber };
    const entries = batch.events.map((event) => ({
      event,
      size: encodedLength(JSON.stringify(event)),
    }));

    flushChain = flushChain.then(() =>
      flushBatch({
        entries,
        errorsInBatch: batch.hasErrors,
        sessionId: batch.sessionId,
        userId: batch.userId,
        userEmail: currentUserEmail,
        counter,
        // The page these events were recorded on, not the one the tab is
        // showing now.
        pageUrl: batch.pageUrl,
        attempts: batch.attempts ?? 0,
      }).catch((err) => {
        console.warn("[rrweb] Retry of a held batch failed:", err?.message ?? err);
      }),
    );
  }
}

// The same defect once more, and the one that fires on every logout and every
// route-away that stops recording with a non-empty buffer. `scheduleFlush` put
// the final flush on the chain, so it ran a microtask later, by which time this
// function had already nulled `currentSessionId`; `flushEvents` opened with a
// guard on that global, found no session, and returned having sent nothing. The
// buffer was then cleared by the next `startRecording`. Every event since the
// last periodic flush went, silently, on the way out of the door.
//
// The buffer and the session id are now taken here, synchronously, and handed to
// the flush, so nothing the flush depends on can be reassigned under it. It is
// still put on the chain rather than awaited, because this is called from
// synchronous teardown and cannot block; what changed is that it now has
// everything it needs by the time it runs.
//
// A failure of this last flush still cannot be requeued, because there is no
// longer a buffer belonging to this recording to requeue into. It is no longer
// simply lost either: `abandonBatch` writes it to sessionStorage under its own
// session id and sequence number, and the next `startRecording` in this tab
// resends it. One attempt on the way out, and a second one on the way back in,
// which is the attempt this path never had.
export function stopRecording() {
  if (stopRecordingFn) {
    stopRecordingFn();
    stopRecordingFn = null;
  }
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }

  const entries = bufferedEntries;
  const errorsInBatch = hasErrorsInBatch;
  const closingSessionId = currentSessionId;
  const closingUserId = currentUserId;
  const closingUserEmail = currentUserEmail;
  const closingCounter = sequenceCounter;
  bufferedEntries = [];
  bufferedBytes = 0;
  hasErrorsInBatch = false;

  window.removeEventListener("beforeunload", handleBeforeUnload);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  currentSessionId = null;

  if (entries.length > 0 && closingSessionId) {
    flushChain = flushChain.then(() =>
      flushBatch({
        entries,
        errorsInBatch,
        sessionId: closingSessionId,
        userId: closingUserId,
        userEmail: closingUserEmail,
        counter: closingCounter,
        // The page the recording was on, read now rather than when the flush
        // gets its turn on the chain: a logout redirect is exactly the kind of
        // thing that runs in between.
        pageUrl: getCurrentPageUrl(),
      }).catch((err) => {
        console.warn("[rrweb] Final flush failed:", err?.message ?? err);
      }),
    );
  }

  console.log("[rrweb] Recording stopped");
}
